import { useEffect, useRef, useState } from "react"
import { Send, Check, CheckCheck, MessageSquareReply } from "lucide-react"
import { cn } from "@/lib/utils"
import { Contact, Message } from "./types"
import { Switch } from "@/components/ui/switch"
import { Label } from "@/components/ui/label"
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu"

interface ChatPanelProps {
  contact: Contact | null
  messages: Message[]
  onSend: (contactId: string, content: string, endChat: boolean) => void
}

function MessageStatusIcon({ status }: { status: Message["status"] }) {
  if (status === "sent") return <Check className="h-3 w-3 text-muted-foreground" />
  if (status === "delivered") return <CheckCheck className="h-3 w-3 text-muted-foreground" />
  return <CheckCheck className="h-3 w-3 text-primary" />
}

const QUICK_REPLIES = [
  "Thank you for reaching out! How can I help you?",
  "I'll look into this and get back to you shortly.",
  "Could you please provide more details?",
  "Is there anything else I can help you with?",
]

export function ChatPanel({ contact, messages, onSend }: ChatPanelProps) {
  const [input, setInput] = useState("")
  const [endChat, setEndChat] = useState(false)
  const scrollRef = useRef<HTMLDivElement>(null)
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight
  }, [messages, contact?.id])

  function handleToggleEndChat() {
    setEndChat(!endChat)
  }

  function handleSend() {
    if (!input.trim() || !contact) return
    onSend(contact.id, input.trim(), endChat)
    setInput("")
    if (textareaRef.current) textareaRef.current.style.height = "auto"
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
  }

  function handleInputChange(e: React.ChangeEvent<HTMLTextAreaElement>) {
    setInput(e.target.value)
    e.target.style.height = "auto"
    e.target.style.height = `${Math.min(e.target.scrollHeight, 120)}px`
  }

  if (!contact) {
    return (
      <div className="flex flex-col items-center justify-center h-full bg-muted/30 gap-4">
        <div className="h-20 w-20 rounded-full bg-muted flex items-center justify-center">
          <Send className="h-8 w-8 text-muted-foreground" />
        </div>
        <div className="text-center">
          <p className="text-foreground text-xl font-light">Chat</p>
          <p className="text-muted-foreground text-sm mt-1">Select a conversation or Wait for events to arrive.</p>
        </div>
      </div>
    )
  }

  return <div className="flex flex-col h-full bg-background">
    {/* Chat header */}
    <div className="flex items-center gap-3 px-4 py-2 border-b border-border bg-background shrink-0">
      <div className="relative shrink-0">
        <div className={cn("h-9 w-9 rounded-full flex items-center justify-center text-white font-semibold text-sm")}>
          <div className="text-2xl">{contact.type}</div>
          {/* {contact.type} */}
        </div>
      </div>
      <div className="flex-1 min-w-0 flex items-center gap-1.5 flex-wrap">
        <p className="text-foreground text-sm font-semibold shrink-0">{contact.name}</p>
        {contact.taxonomy.map((taxonomy) => {
          return (
            <span key={taxonomy.name} className="text-sm bg-muted px-2 py-1 rounded-full font-medium shrink-0">
              {taxonomy?.emoji} {taxonomy.name}
            </span>
          )
        })}
      </div>
      <div className="flex items-center gap-0.5">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="p-2 rounded-full hover:bg-muted transition-colors text-muted-foreground hover:text-foreground">
              <MessageSquareReply className="h-4 w-4" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {QUICK_REPLIES.map((reply) => (
              <DropdownMenuItem key={reply} onSelect={() => setInput(reply)}>
                {reply}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>

    {/* Messages area */}
    <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-3 space-y-1 bg-muted/20">
      {messages.length === 0 ? (
        <div className="flex items-center justify-center h-full">
          <div className="bg-muted text-muted-foreground text-xs px-4 py-2 rounded-lg">
            No messages yet. Say hello!
          </div>
        </div>
      ) : (
        messages.map((msg, idx) => {
          const isOutgoing = msg.direction === "outgoing"
          const prevMsg = messages[idx - 1]
          const showDateSep = !prevMsg || prevMsg.timestamp !== msg.timestamp
          let role = ""
          if (msg.role === "assistant") role = "🤖 "
          return <div key={msg.id}>
            {showDateSep && idx > 0 && (
              <div className="flex justify-center my-3">
                <span className="bg-muted text-muted-foreground text-[11px] px-3 py-1 rounded-full">{msg.timestamp}</span>
              </div>
            )}
            <div className={cn("flex mb-1", isOutgoing ? "justify-end" : "justify-start")}>
              <div className={cn(
                "max-w-[65%] rounded-2xl px-3 py-2 text-sm shadow-xs",
                isOutgoing
                  ? "bg-primary text-primary-foreground rounded-tr-sm"
                  : "bg-background border border-border text-foreground rounded-tl-sm"
              )}>
                <p className="wrap-break-word whitespace-pre-wrap leading-relaxed">{role}{msg.content}</p>
                <div className={cn("flex items-center gap-1 mt-1", isOutgoing ? "justify-end" : "justify-start")}>
                  <span className={cn("text-[10px]", isOutgoing ? "text-primary-foreground/70" : "text-muted-foreground")}>
                    {msg.timestamp}
                  </span>
                  {isOutgoing && <MessageStatusIcon status={msg.status} />}
                </div>
              </div>
            </div>
          </div>

        })
      )}
    </div>

    {/* Input area */}
    <div className="flex items-end gap-2 px-3 py-3 border-t border-border bg-background shrink-0">
      <div className="flex items-center gap-2 shrink-0 mb-3">
        <Label htmlFor="endconvo">End Chat</Label>
        <Switch id="endconvo" onCheckedChange={handleToggleEndChat} checked={endChat} />
      </div>
      {/* <button className="p-2 rounded-full hover:bg-muted transition-colors text-muted-foreground hover:text-foreground shrink-0">
        <Smile className="h-5 w-5" />
      </button>
      <button className="p-2 rounded-full hover:bg-muted transition-colors text-muted-foreground hover:text-foreground shrink-0">
        <Paperclip className="h-5 w-5" />
      </button> */}

      <div className="flex-1 bg-muted rounded-lg px-3 py-2 border border-input">
        <textarea
          ref={textareaRef}
          value={input}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          placeholder="Type a message"
          rows={1}
          className="w-full bg-transparent text-foreground placeholder:text-muted-foreground text-sm resize-none outline-hidden leading-relaxed"
          style={{ minHeight: "22px", maxHeight: "120px" }}
        />
      </div>

      {input.trim() ? (
        <button onClick={handleSend} className="p-2.5 rounded-full bg-primary hover:opacity-90 transition-opacity text-primary-foreground shrink-0">
          <Send className="h-4 w-4" />
        </button>
      ) : (
        <button disabled className="p-2.5 rounded-full bg-muted hover:opacity-90 transition-opacity text-stone-400 shrink-0">
          <Send className="h-4 w-4" />
        </button>
      )}
    </div>
  </div>

}
